import React, { useEffect, useState } from 'react';
import flatpickr from 'flatpickr';
import { useParams, useNavigate } from 'react-router-dom';
import { CONFIG } from "../config"; // Ensure CONFIG has API_BASE_URL and VEHICLE_DETAILS_URL
import Header from '../components/Header';
import WorkOrderTimeline from '../components/WorkOrderTimeline';
import MonthlyMaintenanceCost from '../components/MonthlyMaintenanceCost';
import ComponentsHealth from '../components/ComponentsHealth';
import DatePickerInput from '../components/DatePickerInput';
import VehicleAiRecommendations from '../components/VehicleAiRecommendations';
import '../styles/styles.css';
import VehiclePredictedRiskDetails from '../components/VehiclePredictedRiskDetails';
import posthog from 'posthog-js';


const VehicleDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // get vehicle id from URL
  const formatDateToMDY = (date) => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const today = new Date(); // Current date and time (July 7, 2025, 01:44 PM IST)
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const defaultRestrictedDate = formatDateToMDY(yesterday); // "07/06/2025"
  const defaultUnrestrictedDate = "04/30/2025";

  // Check if user is restricted
  const restrictedIds = JSON.parse(process.env.REACT_APP_RESTRICTED_USER_ID || '[]');
  const restrictedDevice = process.env.REACT_APP_RESTRICTED_DEVICE || '';
  const deviceProvider = localStorage.getItem("tensor-user-device-provider");
  const userId = parseInt(localStorage.getItem("tensor-user-id"), 10);
  //const isRestricted = deviceProvider === restrictedDevice;
  const isRestricted = deviceProvider === restrictedDevice;

  const [selectedDate, setSelectedDate] = useState(() => {
    const savedDate = localStorage.getItem('fleet-selected-date');
    const isValidSavedDate = savedDate && !isNaN(new Date(savedDate).getTime()) && new Date(savedDate) >= new Date('2025-05-15') && new Date(savedDate) <= yesterday;
    return isValidSavedDate ? savedDate : (isRestricted ? defaultRestrictedDate : defaultUnrestrictedDate);
  });

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVehicleDetails = async () => {
      setLoading(true);
      try {
        // Adjust the URL as per your API requirements
        const response = await fetch(
          `${CONFIG.API_BASE_URL}/${CONFIG.VEHICLE_DETAILS_URL}/${localStorage.getItem(
            "tensor-user-id"
          )}?vehicle_id=${id}&date_str=${selectedDate}`,
          {
            headers: {
              "Content-Type": "application/json",
              "Authorization": "Bearer " + localStorage.getItem("tensor-auth-token"),
            },
          }
        );
        if (response.status === 401) {
          localStorage.removeItem('tensor-auth-token');
          localStorage.removeItem('tensor-user-id');
          navigate('/', { state: { message: 'Session expired please try again.', type: 'error' } });
          return;
        }
        const data = await response.json();
        if (data.success && data.data) {
          setVehicle(data.data);
        } else {
          setError("Vehicle not found.");
        }
      } catch (err) {
        console.error("Failed to fetch vehicle details:", err);
        setError("Failed to fetch vehicle details.");
      } finally {
        setLoading(false);
      }
    };

    fetchVehicleDetails();
  }, [id, selectedDate]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    localStorage.setItem('fleet-selected-date', date);
  };

  useEffect(() => {
    const startTime = Date.now();

    return () => {
      const duration = Math.floor((Date.now() - startTime) / 1000); // in seconds
      try {
        posthog.capture('time_spent_on_vehicle_health_page', {
          vehicle_id: id,
          vehicle_number: vehicle?.overview?.vehicle_number || 'NA',
          user_id: userId,
          duration_seconds: duration,
        });
      } catch (err) {
        console.warn("PostHog event failed:", err);
      }
    };
  }, [id, vehicle?.overview?.vehicle_number, userId]);


  return (
    <div className="min-h-screen body_bg_color">
      <Header />

      <main className="max-w-7xl mx-auto px-1 py-6">
        {/* Top Section: Header & Date Picker */}

        {loading && (
          <section class="flex items-start justify-center min-h-screen pt-48 bg-gray-100">
            <div class="text-center">
              <div class="flex justify-center mb-4">
                <svg class="w-12 h-12 text-blue-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z">
                  </path>
                </svg>
              </div>
              <h2 class="text-xl md:text-xl font-semibold text-gray-800 animate-pulse">Fetching vehicle details...</h2>
              <p class="text-sm text-gray-500 mt-2">Please wait while we load everything up for you <i className="ti ti-truck-delivery mt-2"></i></p>
            </div>
          </section>
        )}

        {!loading && (
          <DatePickerInput
            headerText='Vehicle Health'
            initialDate={selectedDate}
            onDateChange={handleDateChange}
            showFilterByVehicleNumber={true}
            isRestricted={isRestricted} // Pass isRestricted prop
          />
        )}
        {!loading && (
          <>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div class="flex flex-col gap-4">
                <div class="bg-white">
                  {vehicle?.overview && (
                    <div class="px-4 pt-3 pb-1 v_health">
                      <h2 class="text-gray-700 font-semibold mb-2 v_health_blocks_heading">Overview</h2>
                      <div class="flex flex-col md:flex-row">
                        <div class="w-full md:w-1/2 pt-2 pr-2 pb-2 pl-0">
                          <span class="w-24 inline-block v_health_overview_text_key">Vehicle ID:</span>
                          <span class="font-semibold v_health_overview_text_value">{vehicle?.overview?.vehicle_number ?? 'NA'}</span>
                        </div>
                        <div class="w-full md:w-1/2 pt-2 pr-2 pb-2 pl-0">
                          <span class="w-24 inline-block v_health_overview_text_key">Age:</span>
                          <span class="font-semibold v_health_overview_text_value">
                            {vehicle?.overview?.age != null ? `${vehicle.overview.age} ${vehicle.overview.age >= 2 ? 'years' : 'year'}` : 'NA'}
                          </span>
                        </div>
                      </div>
                      <div class="flex flex-col md:flex-row">
                        <div class="w-full md:w-1/2 pt-2 pr-2 pb-2 pl-0 flex">
                          <span class="w-24 inline-block v_health_overview_text_key align-top">Model & Make:</span>
                          <span class="font-semibold v_health_overview_text_value">{vehicle?.overview?.model ?? 'NA'} {vehicle?.overview?.make ?? 'NA'}</span>
                        </div>
                        <div class="w-full md:w-1/2 pt-2 pr-2 pb-2 pl-0 flex">
                          <span class="w-24 shrink-0 inline-block v_health_overview_text_key">Location:</span>
                          <span class="font-semibold v_health_overview_text_value break-words">
                            {vehicle?.overview?.location ?? 'NA'}
                          </span>
                        </div>
                      </div>
                      <div class="flex flex-col md:flex-row">
                        <div class="w-full md:w-1/2 pt-2 pr-2 pb-2 pl-0 flex">
                          <span class="w-24 inline-block v_health_overview_text_key">Status:</span>
                          {vehicle?.overview?.status && (
                            <span
                              className={`status_icon inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-md ${vehicle.overview.status.toLowerCase() === "healthy"
                                  ? "text-green-900 bg-green-200"
                                  : vehicle.overview.status.toLowerCase() === "at_risk"
                                    ? "text-yellow-900 bg-yellow-300"
                                    : "text-red-900 bg-red-300"
                                }`}
                            >
                              {vehicle.overview.status.toLowerCase() === "healthy" && (
                                <i className="ti ti-circle-check" />
                              )}
                              {vehicle.overview.status.toLowerCase() === "at_risk" && (
                                <i className="ti ti-alert-triangle" />
                              )}
                              {vehicle.overview.status.toLowerCase() === "down" && (
                                <i className="ti ti-circle-x" />
                              )}
                              {vehicle.overview.status
                                .split("_")
                                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(" ")}
                            </span>
                          )}
                        </div>
                        <div class="w-full md:w-1/2 pt-2 pr-2 pb-2 pl-0">
                          <span class="w-24 inline-block v_health_overview_text_key">Mileage:</span>
                          <span className="font-semibold v_health_overview_text_value">
                            {vehicle?.overview?.mileage != null
                              ? `${new Intl.NumberFormat().format(vehicle.overview.mileage)} miles`
                              : 'NA'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div class="">
                  {vehicle?.workorders && (
                    <WorkOrderTimeline data={vehicle.workorders} />
                  )}
                </div>
                <div class="">
                  {vehicle?.monthly_maintainnce_cost && (
                    <MonthlyMaintenanceCost data={vehicle.monthly_maintainnce_cost} />
                  )}
                </div>
              </div>

              <div class="h-full">
                <div class="bg-white p-4 rounded shadow">
                  <VehicleAiRecommendations vehicleId={id} selectedDate={selectedDate} />
                </div>
                <VehiclePredictedRiskDetails vehicleId={id} selectedDate={selectedDate} />
              </div>

            </div>
            {vehicle?.component_health && (
              <div class="mt-5">
                <ComponentsHealth data={vehicle.component_health} />
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 text-center text-gray-600">
          © 2025 Tensor Planet. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default VehicleDetails;